import { encode } from '../qr'

/** A QR, as one SVG path.
 *
 *  Drawn here rather than fetched, and that is a security property rather than
 *  a preference: a server-rendered QR is an addressable object whose URL is, or
 *  can be traded for, a live enrollment code. This one exists as a path in the
 *  document for as long as the screen is open and has no address at all.
 *
 *  One `<path>` of rectangles rather than a grid of `<rect>` elements — a
 *  version-3 symbol is 841 modules, and four hundred DOM nodes to draw a square
 *  is four hundred more than it needs.
 */
export function Qr({ text, size = 220 }: { text: string; size?: number }) {
  let grid: boolean[][]
  try {
    grid = encode(text)
  } catch {
    // Never expected: the caller passes a link this product produced. A blank
    // box is still better than a blank screen if it ever happens.
    return null
  }
  const n = grid.length
  const quiet = 4                 // the standard's margin; scanners need it
  const span = n + quiet * 2

  const d: string[] = []
  for (let r = 0; r < n; r++) {
    // Runs rather than modules: horizontally adjacent dark cells become one
    // rectangle, which roughly halves the path on a typical symbol.
    let c = 0
    while (c < n) {
      if (!grid[r]![c]) { c++; continue }
      let end = c
      while (end + 1 < n && grid[r]![end + 1]) end++
      d.push(`M${c + quiet} ${r + quiet}h${end - c + 1}v1h-${end - c + 1}z`)
      c = end + 1
    }
  }

  return (
    <svg
      viewBox={`0 0 ${span} ${span}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR"
      /* White explicitly, not transparent. These get held up on a phone in a
         dark-themed browser, and a scanner needs the quiet zone to be light. */
      style={{ background: '#fff', borderRadius: 8, display: 'block' }}
    >
      <path d={d.join('')} fill="#000" />
    </svg>
  )
}
